import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const TestimonialsSection = () => {
  const { t, language } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  // Responsive visible count
  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth < 768) setVisibleCount(1);
      else if (window.innerWidth < 1024) setVisibleCount(2);
      else setVisibleCount(3);
      setActiveIndex(0);
    };
    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  // Real Google Reviews from Sakthi Hospital's Google My Business
  const googleReviews = [
    {
      id: '1',
      name: 'Shahana Farhana',
      date: 'Jan 2025',
      rating: 5,
      content: 'Recently had a great experience at Sakthi Hospital and Dr. Leena Rajam was very knowledgeable and took the time to answer all my questions. The staffs was incredibly caring and attentive throughout my stay, the facility was clean and well maintained. I would recommend this hospital to anyone.',
      source: 'Google',
    },
    {
      id: '2',
      name: 'M Shabeer',
      date: 'Mar 2025',
      rating: 5,
      content: 'Multi Speciality Hospital with experienced doctors and humble staffs. Specialized in CKD treatment. Insurance process is also good and seamless. Overall a very great Hospital at our Triplicane locality.',
      source: 'Google',
    },
    {
      id: '3',
      name: 'Abul Hasan',
      date: 'Mar 2025',
      rating: 5,
      content: 'Visited for a ENT issue on a Sunday evening, Front office staff arranged timely consultation appointment with Dr. Leena Rajam. Dr. arranged for procedure on the same Sunday evening, being concerned about the wellness. Staff & Nurses were good to help & guide. Thank you.',
      source: 'Google',
    },
    {
      id: '4',
      name: 'Vijay Anand',
      date: 'Mar 2025',
      rating: 5,
      content: 'It was a nice experience, the staffs were cordial. The nurse and Doctor were supportive during my procedure. Thanks to them.',
      source: 'Google',
    },
    {
      id: '5',
      name: 'Anand Sekar',
      date: 'May 2025',
      rating: 5,
      content: 'Reception guys are awesome and provide the good support and doctors also very friendly manner to check our problems and providing the appropriate treatment.',
      source: 'Google',
    },
    {
      id: '6',
      name: 'Karthik Rangasamy',
      date: '2025',
      rating: 5,
      content: 'Staffs and doctors has done an excellent job. Good service.',
      source: 'Google',
    },
    {
      id: '7',
      name: 'Saran',
      date: 'Dec 2024',
      rating: 5,
      content: 'Excellent professional counselling at Sakthi Hospital and Research Centre. The staff provided great support and guidance throughout my treatment. Highly recommended for anyone seeking expert advice and care.',
      source: 'Google',
    },
    {
      id: '8',
      name: 'Muthaiyan',
      date: '2023',
      rating: 5,
      content: 'I recently had a health checkup at Sakthi Hospital and I must say I was impressed with the level of service I received. The staff were friendly and attentive, and the facility was clean and well-maintained. Would highly recommend.',
      source: 'Google',
    },
    {
      id: '9',
      name: 'Hari Priya',
      date: 'May 2025',
      rating: 4,
      content: 'Very Good hospital, they are moving all the procedures fastly.',
      source: 'Google',
    },
  ];

  // Show responsive number of reviews
  const maxIndex = Math.max(0, googleReviews.length - visibleCount);

  const nextSlide = useCallback(() => {
    setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = () => {
    setActiveIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const initialsColors = [
    'bg-[#0066b3]', 'bg-[#00a0e3]', 'bg-[#004a82]', 'bg-[#1a8a5c]',
    'bg-[#c41e3a]', 'bg-[#d4a24c]', 'bg-[#7c3aed]', 'bg-[#0066b3]', 'bg-[#00a0e3]'
  ];

  return (
    <section id="testimonials" className="py-14 bg-[#f8fafc]" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[#0066b3] mb-3">
            <div className="w-8 h-[2px] bg-[#0066b3]"></div>
            <span className="text-xs font-semibold uppercase tracking-wider">
              {language === 'ta' ? 'Google மதிப்புரைகள்' : 'Google Reviews'}
            </span>
            <div className="w-8 h-[2px] bg-[#0066b3]"></div>
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#0f172a] tracking-tight mb-4">
            {t('testimonials.title')}
          </h2>

          {/* Google Rating Badge */}
          <div className="inline-flex items-center gap-3 bg-white rounded-2xl px-6 py-3 shadow-md border border-gray-100 mt-4" data-testid="google-rating-badge">
            <svg viewBox="0 0 24 24" className="w-7 h-7" aria-label="Google">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-1.5">
                <span className="text-2xl font-bold text-[#1a2b3c]">4.2</span>
                <div className="flex">
                  {[1,2,3,4].map(i => (
                    <Star key={`rating-${i}`} size={16} fill="#d4a24c" stroke="#d4a24c" />
                  ))}
                  <Star size={16} fill="#d4a24c" stroke="#d4a24c" style={{clipPath: 'inset(0 80% 0 0)'}} />
                </div>
              </div>
              <span className="text-xs text-[#5a6b7c] font-medium">767+ Google Reviews</span>
            </div>
          </div>
        </div>

        {/* Reviews Carousel */}
        <div className="relative overflow-hidden">
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${activeIndex * (100 / visibleCount)}%)` }}
          >
            {googleReviews.map((review, index) => (
              <div 
                key={review.id}
                className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-3"
              >
                <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 h-full flex flex-col hover:shadow-lg transition-shadow duration-300" data-testid={`review-card-${index}`}>
                  {/* Google Icon + Source */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1.5">
                      <svg viewBox="0 0 24 24" className="w-5 h-5">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span className="text-xs font-medium text-[#5a6b7c]">{review.source}</span>
                    </div>
                    <span className="text-xs text-[#94a3b8]">{review.date}</span>
                  </div>

                  {/* Rating Stars */}
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={`filled-${i}`} size={16} fill="#d4a24c" stroke="#d4a24c" />
                    ))}
                    {[...Array(5 - review.rating)].map((_, i) => (
                      <Star key={`empty-${i}`} size={16} fill="none" stroke="#d4a24c" />
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="flex-1 mb-4">
                    <Quote size={20} className="text-[#0066b3]/20 mb-2" />
                    <p className="text-[#3a4a5c] leading-relaxed text-sm">
                      {review.content}
                    </p>
                  </div>

                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <div className={`w-10 h-10 ${initialsColors[index % initialsColors.length]} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                      {getInitials(review.name)}
                    </div>
                    <div>
                      <div className="font-semibold text-[#1a2b3c] text-sm">
                        {review.name}
                      </div>
                      <div className="text-xs text-[#94a3b8]">Patient</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={prevSlide}
            className="w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-[#0066b3] hover:text-white transition-colors border border-gray-100"
            data-testid="testimonial-prev-btn"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: maxIndex + 1 }).map((_, index) => (
              <button
                key={`dot-${index}`}
                onClick={() => setActiveIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === activeIndex 
                    ? 'w-8 bg-[#0066b3]' 
                    : 'w-2 bg-[#d0e3ed] hover:bg-[#0066b3]/50'
                }`}
                data-testid={`testimonial-dot-${index}`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            className="w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-[#0066b3] hover:text-white transition-colors border border-gray-100"
            data-testid="testimonial-next-btn"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* View All on Google CTA */}
        <div className="text-center mt-8">
          <a
            href="https://www.google.com/maps/place/Sakthi+Hospital/data=!4m2!3m1!1s0x3a526605d0a030d5:0x6e079bd99529d311"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#0066b3] font-semibold text-sm hover:underline"
            data-testid="view-all-google-reviews"
          >
            {language === 'ta' ? 'Google-ல் அனைத்து மதிப்புரைகளையும் பார்க்கவும்' : 'View all reviews on Google'}
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
